import axios from "axios";
import FileSaver from "file-saver";
import { defaultServiceContext } from "../contexts";
import DesignerSetting from "../DesignerSetting";
import DocumentMigration from "./DocumentMigration";
import { Constant } from '../const';
import { incrementVersion } from "./functions";
import Utility from "./Utility";
import WorkerManager from "./workers/WorkerManager";

/**
 * Make types available in JSDocs without importing them since this is not a TS file.
 *
 * @import {
 *   Design
 * } from '../types';
 */

export default class CollectionManager {
    /**
     * Global context for managing apps and data storage
     * @module CollectionManager
     */
    constructor({
        avsService,
        environmentService,
        lokiService,
        userService
    } = defaultServiceContext) {
        this._avsService = avsService;
        this._environmentService = environmentService;
        this._lokiService = lokiService;
        this._userService = userService;
        this.workers = new WorkerManager();
        // placeholder user
        this.user = {
            userGroup: "manager"
        };
    }

    /** Load local storage */
    init() {
        // Load language resource
        if (!window.localStorage.getItem("lang")) {
            window.localStorage.setItem("lang", "en");
        }
        try {
            window.l = Utility.mergeDeep(
                require("../languages/en.json"),
                require("../languages/" + window.localStorage.getItem("lang") + ".json")
            );
        } catch (e) {
            window.l = require("../languages/en.json");
        }
    }

    requestFormList(callback, lastArray = [], number = 0) {
        const devEnvironments = new Set([
            'aqa',
            'dev'
        ]);
        const env = DesignerSetting.s.env.toLowerCase();
        const formServiceEnv = (devEnvironments.has(env)) ? 'dev' : 'prod';
        const data = {
            dataOption: "all",
            filters: [
                {
                    key: "client",
                    operator: "eq",
                    val: window.apm.serialized.client
                },
                {
                    key: "application",
                    operator: "eq",
                    val: window.apm.serialized.client + window.apm.serialized.application
                },
                {
                    key: "environment",
                    operator: "eq",
                    val: formServiceEnv
                },
                {
                    key: "formType",
                    operator: "eq",
                    val: "DELEGATE"
                },
                {
                    key: "isDeleted",
                    operator: "eq",
                    val: "false"
                }
            ],
            page: {
                number,
                size: 500
            },
            sort: [
                {
                    sortKey: "name",
                    order: "ASC"
                }
            ]
        };

        const headers = {
            "Content-Type": "application/json",
            "environment": formServiceEnv
        };
        const { access_token } = this._userService.user ?? {};
        if (access_token) {
            headers["Authorization"] = "Bearer " + access_token;
        }

        axios({
            method: "POST",
            headers,
            data,
            url: `/forms/api/forms/filter`,
            responseType: "json"
        }).then(res => {
            if (!res.data.last) {
                return this.requestFormList(callback, [...lastArray, ...res.data.content], number + 1)
            } else {
                return callback([...lastArray, ...res.data.content]);
            }
        }).catch(err => {
            console.error(err);
            callback((err.response && err.response.data) ? new Error(err.response.data.error) : err);
        });
    }

    /**
     * Save to AVS with Collabroation.
     *
     * @param {string} commitMessage Commit message for the change.
     * @param {Object} changeLogMap Changelogs for the save that is currently happening.
     * @param {Object} mergedVersion Version of the application to save.
     * @param {string} newVersionNumber Version number to create if @see saveAsNewVersion is `true`.
     * @param {boolean} saveAsNewVersion Saves a new version instead of bumping an existing version of the application.
     */
    async saveWithCollaboration({
        changeLogMap,
        commitMessage,
        mergedVersion,
        newVersionNumber,
        saveAsNewVersion
    }) {
        const timeId = 'Save With Collaboration';
        console.time(timeId);
        const { branchId } = mergedVersion;
        // TODO: check if window.apm.changeLogMap[0].version matches window.apm.leatestVersion?
        const latestVersion = (
            await this._avsService.getLatestVersion(branchId)
        )?.version;
        let changeLogMaps = await this._avsService.getChangeLogs(
            branchId,
            latestVersion
        );
        mergedVersion.lastUpdate = window.apm.timestamp;

        let version = null;
        if (saveAsNewVersion) {
            version = newVersionNumber;
        } else {
            // Get the latest version from AVS and update the latest version by 1 if not saving as a new version
            version = incrementVersion(latestVersion ?? mergedVersion.version);
        }

        const newEntry = window.apm.getChangeLogEntry({
            author: mergedVersion.author,
            changeLogMap,
            commitMessage,
            version
        });

        mergedVersion.version = version;
        mergedVersion.repoVersion = version;
        // Maintain limit of 20 entries.
        changeLogMaps = [newEntry, ...changeLogMaps].slice(0, 20);

        // TODO: Refactor this.
        const { default: SaveToRepository } = await import("./SaveToRepository");
        const { default: SaveWidget } = await import("../widgets/SaveWidget");
        const serialized = await SaveToRepository.saveToRepoDesign(
            mergedVersion,
            changeLogMaps
        );

        if (serialized) {
            window.apm.setChangeLogMaps(changeLogMaps, version);
            SaveWidget.instance?.refreshApplicationUIAfterSave(serialized);

            if (serialized.branchingMerging) {
                window.apm.setCurrentBranchName();
            }
        }

        console.timeEnd(timeId);
        return serialized;
    }

    /**
     * Provide new or default timestamp for object props
     *
     * @param {json} obj application json
     * @param {string} timestamp
     * @param {string} propName
     * @param {boolean} isArchetype
     * @return {string} cumputed timestamp
     */
    getTimeStamp(obj, timestamp, propName, isArchetype) {
        if (isArchetype) { // reset all values
            return timestamp;
        }

        if (obj.props) { // only reset if not present
            return (obj.props[propName]) ? obj.props[propName] : timestamp;
        } else {
            return (obj[propName]) ? obj[propName] : timestamp;
        }
    };

    /**
     * Set timestamps for application sub components and their objects
     *
     * @param {json} application
     * @param {boolean} isArchetype
     */
    setApplicationTimestamps(application, isArchetype) {
        const timestamp = window.frontPageTimeStamp || window.apm.timestamp; // called on front page when adding an app to the dashboard
        const subComps = ['components', 'fillerPrompts', 'functions', 'globalIntents', 'compoundIntents'];

        subComps.forEach(subComp => {

            const comp = application[subComp];
            //we can remove this
            // cleaning up legacy typo
            const getFixedTimeAddedToFlowTimeStamp = (obj) => {
                let timeAddedToFlow = null;
                if (obj.props.timeAddedToflow) {
                    timeAddedToFlow = obj.props.timeAddedToflow;
                    delete obj.props.timeAddedToflow;
                }
                else if (!obj.props.timeAddedToFlow) {
                    timeAddedToFlow = obj.props.lastSavedChange;
                }
                else {
                    timeAddedToFlow = obj.props.timeAddedToFlow;
                }

                return timeAddedToFlow;

            };

            if (subComp === 'components') {
                Object.keys(comp).forEach(key => {
                    if (key !== 'modules') {
                        application[subComp][key].forEach(obj => {
                            obj.props.createdTime = this.getTimeStamp(obj, timestamp, 'createdTime', isArchetype);
                            obj.props.lastLocalChange = this.getTimeStamp(obj, timestamp, 'lastLocalChange', isArchetype);
                            obj.props.lastSavedChange = this.getTimeStamp(obj, timestamp, 'lastSavedChange', isArchetype);
                        });
                    } else {
                        application[subComp][key].forEach(mod => {
                            mod.props.timeAddedToFlow = getFixedTimeAddedToFlowTimeStamp(mod);
                            mod.props.lastLocalChange = this.getTimeStamp(mod, timestamp, 'lastLocalChange', isArchetype);
                            mod.props.lastSavedChange = this.getTimeStamp(mod, timestamp, 'lastSavedChange', isArchetype);

                            const flow = mod.versions[1].flow;
                            flow.nodes.forEach(obj => {
                                obj.props.timeAddedToFlow = getFixedTimeAddedToFlowTimeStamp(obj);
                                obj.props.lastLocalChange = this.getTimeStamp(obj, timestamp, 'lastLocalChange', isArchetype);
                                obj.props.lastSavedChange = this.getTimeStamp(obj, timestamp, 'lastSavedChange', isArchetype);
                            });
                            flow.links.forEach(obj => {
                                obj.props.lastLocalChange = this.getTimeStamp(obj, timestamp, 'lastLocalChange', isArchetype);
                                obj.props.lastSavedChange = this.getTimeStamp(obj, timestamp, 'lastSavedChange', isArchetype);
                            });
                        });

                    }

                });
            } else if (subComp === 'fillerPrompts') {
                Object.keys(application[subComp]).forEach(key => {
                    application[subComp][key].forEach(obj => {
                        obj.createdTime = this.getTimeStamp(obj, timestamp, 'createdTime', isArchetype);
                        obj.lastLocalChange = this.getTimeStamp(obj, timestamp, 'lastLocalChange', isArchetype);
                        obj.lastSavedChange = this.getTimeStamp(obj, timestamp, 'lastSavedChange', isArchetype);
                    });
                });
            } else {
                if (application[subComp]) {
                    application[subComp].forEach(obj => {
                        obj.createdTime = this.getTimeStamp(obj, timestamp, 'createdTime', isArchetype);
                        obj.lastLocalChange = this.getTimeStamp(obj, timestamp, 'lastLocalChange', isArchetype);
                        obj.lastSavedChange = this.getTimeStamp(obj, timestamp, 'lastSavedChange', isArchetype);
                    });
                }
            }

        });
        return application;
    }

    getVariable(varIdOrName, variables) {
        for (var i = 0; i < variables.length; i++) {
            if (variables[i].id === varIdOrName || variables[i].props.config.varName === varIdOrName) {
                return variables[i];
            }
        }
    }

    getLocalVariable(varIdOrName, parentModule) {
        var refVars = parentModule.localVariables;
        for (var i = 0; i < refVars.length; i++) {
            if (refVars[i].id === varIdOrName || refVars[i].props.config.varName === varIdOrName) {
                return refVars[i];
            }
        }
    }

    /**
     * Import serialized @see Design.
     *
     * @param {Design} serialized Serialized app model.
     * @param {boolean} isArchetype If true, updated timestamps for entire app, otherwise, just for fields without time stamps.
     * @returns {Promise<Design>}
     */
    async importDocument(serialized, isArchetype = false) {
        serialized = DocumentMigration.upgrade(serialized);
        if (serialized.globalIntents) {
            serialized.globalIntents.forEach(gi => {
                var intIndex = serialized.components.intents.findIndex(i => i.id === gi.intent);
                if (intIndex !== -1) serialized.components.intents[intIndex].isGlobalIntent = true;
            });
        }
        if (serialized.compoundIntents === undefined) {
            serialized.compoundIntents = [];
        }

        for (var type in serialized.fillerPrompts) {
            serialized.fillerPrompts[type].forEach((fillerPrompt) => {
                if (!('externalAudioServiceUrl' in fillerPrompt)) {
                    fillerPrompt.externalAudioServiceUrl = '';
                }
                if (!('externalAudioPrompt' in fillerPrompt)) {
                    fillerPrompt.externalAudioPrompt = false;
                }
            });
        }

        if (serialized.components['modules']) {
            serialized.components['modules'].forEach(mo => {
                var moIndex = serialized.components['modules'].findIndex(m => m.id === mo.id);
                serialized.components['modules'][moIndex].versions[1].flow.nodes[0].props.config.description = "Entry point of the module";
            });
        }
        // this only happend on FrontPage, during import; Data Access child nodes need to have their config updated to parent node
        serialized.components.modules.forEach(module => {
            module.versions[1].flow.nodes.forEach(node => {
                if (node._class === "DataAccessCompletionNodeModel") {
                    var dataIndex = serialized.components.objects.findIndex(ob => ob.id === node.props.parentComponentID);
                    if (dataIndex !== -1 && JSON.stringify(serialized.components.objects[dataIndex].props.config) !== JSON.stringify(node.props.config)) {
                        serialized.components.objects[dataIndex].props.config = node.props.config;
                        serialized.components.objects[dataIndex].props.lastLocalChange = window.frontPageTimeStamp;

                    }
                }
            });
        });

        if (serialized.components['modules']) {
            serialized.components.modules.forEach(module => {
                module.versions[1].flow.nodes.forEach(node => {
                    if (node._class === "PromptNodeModel" && node.props.config && node.props.config.inputs) {
                        node.props.config.inputs.forEach(input => {
                            let sv = input.argumentName.split(".");
                            if (sv[0] === Constant.k.SCOPE_APPLICATION) {
                                const variableIndex = serialized.components.variables.findIndex(va => va.name === sv[1]);
                                if (variableIndex !== -1) {
                                    input.sensitiveData = serialized.components.variables[variableIndex].props.config.sensitiveData;
                                }
                            }
                        });
                    }
                });
            });
        }
        if (serialized.components.objects) {
            serialized.components.objects.forEach(object => {
                if (object._class === "PromptNodeModel" && object.props.config && object.props.config.inputs) {
                    object.props.config.inputs.forEach(input => {
                        let sv = input.argumentName.split(".");
                        if (sv[0] === Constant.k.SCOPE_APPLICATION) {
                            const variableIndex = serialized.components.variables.findIndex(va => va.name === sv[1]);
                            if (variableIndex !== -1) {
                                input.sensitiveData = serialized.components.variables[variableIndex].props.config.sensitiveData;
                            }
                        }
                    });
                }
            });
        }

        // copy graphQLSchemas from appCommonModules
        if (serialized.appCommonModules) {
            serialized.appCommonModules.forEach(cm => {
                if (cm.graphQLSchemas) {
                    cm.graphQLSchemas.forEach(gq => {
                        if (serialized.graphQLSchemas) {
                            var ghIndex = serialized.graphQLSchemas.findIndex(gp => gp.id === gq.id);
                            if (ghIndex === -1) {
                                serialized.graphQLSchemas.push(gq);
                            }
                        }
                        else {
                            serialized.graphQLSchemas = [];
                            serialized.graphQLSchemas.push(gq);
                        }
                    });
                }
            });
        }
        //common modules legacy issue removing subModule related props from components
        if (Utility.getIsCommonModule(serialized)) {
            if (serialized.components.modules) {
                serialized.components.modules.forEach(mo => {
                    delete mo.props.config.subModuleApp;
                    delete mo.props.config.subModuleVersion;
                    delete mo.props.config.subModuleId;
                    mo.versions[1].flow.nodes.forEach(no => {
                        delete no.props.config.subModuleApp;
                        delete no.props.config.subModuleVersion;
                        delete no.props.config.subModuleId;
                    })
                })
            }
            if (serialized.components.objects) {
                serialized.components.objects.forEach(ob => {
                    delete ob.props.config.subModuleApp;
                    delete ob.props.config.subModuleVersion;
                    delete ob.props.config.subModuleId;
                })
            }
            if (serialized.components.intents) {
                serialized.components.intents.forEach(int => {
                    delete int.props.config.subModuleApp;
                    delete int.props.config.subModuleVersion;
                    delete int.props.config.subModuleId;
                })
            }
            if (serialized.graphQLSchemas) {
                serialized.graphQLSchemas.forEach(gs => {
                    delete gs.subModuleApp;
                    delete gs.subModuleVersion;
                    delete gs.subModuleId;
                })
            }
            if (serialized.csvData) {
                serialized.csvData.forEach(cd => {
                    delete cd.subModuleApp;
                    delete cd.subModuleVersion;
                    delete cd.subModuleId;
                })
            }
        }
        serialized = this.setApplicationTimestamps(serialized, isArchetype);
        await this._lokiService.storeSerialized(serialized, { confirmConflict: false });
        return serialized;
    }

    downLoadLatestChangeLogMap(application) {
        const { client, app, version } = application;
        if (window.apm.changeLogMaps) {
            FileSaver.saveAs(
                new Blob(
                    [JSON.stringify(window.apm.changeLogMaps)],
                    { type: "text/plain;charset=utf-8" }),
                `${client}-${app}-${version}-changeLogMaps.json`
            );
        }
    }

    /**
     * Expose a set of global variables in the `window` object.
     * Mainly used for the WalkMe integration.
     */
    exposeGlobalVariables() {
        if (this._userService.isLoggedIn) {
            const currentUser = this._userService.user;

            const roles = (currentUser.authorities ?? []).map(x => x.authority);
            if (currentUser.role) {
                roles.push(currentUser.role);
            }

            // Set global variables
            Object.entries({
                user: {
                    fullName: currentUser.fullName,
                    mail: currentUser.mail,
                    username: currentUser.username,
                },
                uuid: currentUser.username,
                roles
            }).forEach(([key, value]) => {
                window[key] = value;
            });
        }
    }
}
